"use client";

import React from "react";
import { AlertCircle, RefreshCw, MessageSquare, Mail } from "lucide-react";
import { Button } from "./Button";

interface ErrorStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  showSupportFallback?: boolean;
  className?: string;
}

export function ErrorState({
  title = "Something went wrong",
  message = "We encountered a temporary network glitch while processing your request. Please try again.",
  onRetry,
  showSupportFallback = true,
  className = "",
}: ErrorStateProps) {
  return (
    <div
      className={`rounded-2xl border border-rose-500/30 bg-rose-50/40 dark:bg-rose-950/20 p-6 sm:p-8 text-center space-y-4 animate-in fade-in duration-200 ${className}`}
    >
      <div className="mx-auto w-12 h-12 rounded-2xl bg-rose-500/10 text-rose-500 flex items-center justify-center border border-rose-500/20 shadow-md shadow-rose-500/10">
        <AlertCircle className="w-6 h-6" />
      </div>

      <div className="space-y-1 max-w-md mx-auto">
        <h3 className="text-base sm:text-lg font-heading font-bold text-slate-900 dark:text-slate-100">
          {title}
        </h3>
        <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
          {message}
        </p>
      </div>

      <div className="pt-2 flex flex-wrap items-center justify-center gap-3">
        {onRetry && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            className="flex items-center gap-1.5 border-rose-300 dark:border-rose-800 text-rose-600 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-900/40"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            <span>Try Again</span>
          </Button>
        )}

        {showSupportFallback && (
          <a
            href="https://wa.me/919880893416?text=Hi%20Johnson%20Softwares,%20I%20hit%20an%20error%20on%20the%20website%20form."
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button size="sm" variant="gradient" className="flex items-center gap-1.5">
              <MessageSquare className="w-3.5 h-3.5" />
              <span>Contact via WhatsApp</span>
            </Button>
          </a>
        )}
      </div>
    </div>
  );
}
